import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import Button from "@/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube, faVideo } from "@fortawesome/free-brands-svg-icons";
import { useWidgetSettings } from "../WidgetSettings";

// Function to extract YouTube video ID from URL
const extractYouTubeVideoId = (url) => {
  try {
    const urlObj = new URL(url);

    // Handle youtu.be format
    if (urlObj.hostname === "youtu.be") {
      return urlObj.pathname.slice(1);
    }

    // Handle youtube.com format
    if (
      urlObj.hostname === "youtube.com" ||
      urlObj.hostname === "www.youtube.com"
    ) {
      // For watch URLs
      if (urlObj.pathname.includes("/watch")) {
        return urlObj.searchParams.get("v");
      }

      // For YouTube Shorts
      if (urlObj.pathname.includes("/shorts/")) {
        return urlObj.pathname.split("/shorts/")[1].split("?")[0];
      }

      // For shortened URLs
      if (
        urlObj.pathname.includes("/embed/") ||
        urlObj.pathname.includes("/v/")
      ) {
        return urlObj.pathname.split("/").pop();
      }
    }

    return null;
  } catch (error) {
    console.error("Error parsing YouTube URL:", error);
    return null;
  }
};

export default function VideoPlayerConfigForm({ config, onUpdate }) {
  const { setActionButton, clearActionButton, closeSettings } =
    useWidgetSettings();
  const [tempUrl, setTempUrl] = useState(config.videoUrl || "");
  const [error, setError] = useState("");
  const urlInputRef = useRef(null);

  // Use local state for real-time updates
  const [localConfig, setLocalConfig] = useState({
    videoUrl: config.videoUrl || "",
    videoId: config.videoId || "",
    autoplay: config.autoplay || false,
    muted: config.muted !== undefined ? config.muted : true,
    source: config.source || "youtube",
    embedCode: config.embedCode || "",
  });

  const { videoUrl, videoId, autoplay, muted, source } = localConfig;

  const handleUrlChange = (e) => {
    setTempUrl(e.target.value);
    setError("");
  };

  const handleUrlSubmit = useCallback(() => {
    // Basic URL validation
    let formattedUrl = tempUrl.trim();

    if (!formattedUrl) {
      // Allow removing URL
      onUpdate({
        videoUrl: "",
        videoId: "",
        embedCode: "",
      });
      return;
    }

    // Add https:// if not present
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = `https://${formattedUrl}`;
    }

    try {
      // Test if URL is valid
      new URL(formattedUrl);
    } catch (e) {
      setError("Invalid URL format");
      return;
    }

    // Extract YouTube video ID if it's a YouTube URL
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/i;
    let newVideoId = videoId;
    let newSource = source;

    if (youtubeRegex.test(formattedUrl)) {
      newSource = "youtube";
      newVideoId = extractYouTubeVideoId(formattedUrl);

      if (!newVideoId) {
        setError("Could not extract video ID from YouTube URL");
        return;
      }
    } else {
      setError("Currently only YouTube URLs are supported");
      return;
    }

    // Create the embed URL
    const embedUrl = `https://www.youtube.com/embed/${newVideoId}?autoplay=${
      autoplay ? "1" : "0"
    }&mute=${muted ? "1" : "0"}`;

    // Update both local state and parent config
    const updatedConfig = {
      videoUrl: embedUrl,
      videoId: newVideoId,
      source: newSource,
      embedCode: `<iframe width="560" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="background-color: #000000;"></iframe>`,
      autoplay,
      muted,
    };

    setLocalConfig((prev) => ({ ...prev, ...updatedConfig }));
    onUpdate(updatedConfig);

    setTempUrl(formattedUrl);
    setError("");

    // Close the settings panel after successful save
    setTimeout(() => {
      closeSettings();
    }, 100);
  }, [tempUrl, videoId, source, autoplay, muted, onUpdate, closeSettings]);

  const handleAutoplayChange = useCallback(
    (checked) => {
      const newAutoplay = checked;

      // Update local state immediately for real-time UI updates
      setLocalConfig((prev) => ({ ...prev, autoplay: newAutoplay }));

      // Always update the widget configuration immediately
      if (videoId) {
        const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=${
          newAutoplay ? "1" : "0"
        }&mute=${muted ? "1" : "0"}`;

        const updatedConfig = {
          autoplay: newAutoplay,
          videoUrl: embedUrl,
          embedCode: `<iframe width="560" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="background-color: #000000;"></iframe>`,
        };

        onUpdate(updatedConfig);
      } else {
        onUpdate({ autoplay: newAutoplay });
      }
    },
    [videoId, muted, onUpdate]
  );

  const handleMutedChange = useCallback(
    (checked) => {
      const newMuted = checked;

      // Update local state immediately for real-time UI updates
      setLocalConfig((prev) => ({ ...prev, muted: newMuted }));

      // Always update the widget configuration immediately
      if (videoId) {
        const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=${
          autoplay ? "1" : "0"
        }&mute=${newMuted ? "1" : "0"}`;

        const updatedConfig = {
          muted: newMuted,
          videoUrl: embedUrl,
          embedCode: `<iframe width="560" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="background-color: #000000;"></iframe>`,
        };

        onUpdate(updatedConfig);
      } else {
        onUpdate({ muted: newMuted });
      }
    },
    [videoId, autoplay, onUpdate]
  );

  // Set up action button
  useEffect(() => {
    const actionButton = (
      <button
        type="button"
        onClick={handleUrlSubmit}
        className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        <FontAwesomeIcon icon={faYoutube} className="w-4 h-4" />
        {videoUrl ? "Update Video" : "Add Video"}
      </button>
    );

    setActionButton(actionButton);

    return () => {
      clearActionButton();
    };
  }, [setActionButton, clearActionButton, handleUrlSubmit, videoUrl]);

  return (
    <div className="space-y-3 w-full">
      <div>
        <Label className="text-white mb-1.5 flex items-center gap-2 text-sm">
          <FontAwesomeIcon icon={faYoutube} className="text-red-500 w-3 h-3" />
          YouTube Video URL
        </Label>
        <div className="space-y-1.5">
          <Input
            ref={urlInputRef}
            type="url"
            value={tempUrl}
            onChange={handleUrlChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUrlSubmit();
              }
            }}
            placeholder="https://www.youtube.com/watch?v=..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/50 text-sm h-8"
          />
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      </div>

      {videoId && (
        <div className="p-2 bg-white/5 rounded border border-white/10">
          <p className="text-xs text-green-400">✓ Video loaded</p>
        </div>
      )}

      <div className="space-y-2">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-white text-sm">Auto-play video</Label>
            <Switch checked={autoplay} onCheckedChange={handleAutoplayChange} />
          </div>
          {autoplay && (
            <div className="p-2 bg-amber-500/10 rounded border border-amber-500/20">
              <p className="text-xs text-amber-400">
                ⚠️ May slow page load with multiple videos
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-white text-sm">Mute by default</Label>
          <Switch checked={muted} onCheckedChange={handleMutedChange} />
        </div>
      </div>
    </div>
  );
}
